/**
 * ChooseSubscriptionStep - Select a subscription plan
 *
 * Options:
 * - Free: Go directly to dashboard
 * - Paid tiers: Open Stripe checkout, then dashboard on success
 */
import React, { useState } from 'react';
import { useOnboarding } from '../OnboardingWizard';
import { tokens } from '../../../design-system';
import apiClient from '../../../services/unifiedApiClient';

const SUBSCRIPTION_TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    period: '/month',
    description: 'Perfect for trying out Cahoots',
    features: [
      'Event modeling',
      'Features & User Stories',
      'Unlimited projects',
      'Community support',
    ],
    highlighted: false,
    stripePriceId: null, // No payment needed
  },
  {
    id: 'hobbyist',
    name: 'Hobbyist',
    price: '$10',
    period: '/month',
    description: 'For side projects and personal use',
    features: [
      'Everything in Free, plus:',
      'Export to JSON/Markdown',
      'Email support',
    ],
    highlighted: false,
    stripePriceId: 'price_hobbyist', // Replace with actual Stripe price ID
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$50',
    period: '/month',
    description: 'For professional developers',
    features: [
      'Everything in Hobbyist, plus:',
      'GitHub integration',
      'Priority support',
    ],
    highlighted: true,
    badge: 'Most Popular',
    stripePriceId: 'price_pro', // Replace with actual Stripe price ID
  },
];

const ChooseSubscriptionStep = () => {
  const { goToNextStep, updateStepData, setIsLoading, userInfo } = useOnboarding();
  const [selectedTier, setSelectedTier] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSelectTier = async (tier) => {
    setSelectedTier(tier.id);
    setError(null);

    if (!tier.stripePriceId) {
      // Free tier - just record selection and continue
      updateStepData('choose-subscription', {
        selectedTier: tier.id,
        selectedAt: new Date().toISOString(),
      });
      goToNextStep();
      return;
    }

    // Paid tier - initiate Stripe checkout
    setIsProcessing(true);
    try {
      const response = await apiClient.createCheckoutSession({
        priceId: tier.stripePriceId,
        successUrl: `${window.location.origin}/dashboard?subscription=success`,
        cancelUrl: `${window.location.origin}/onboarding?step=subscription`,
      });

      // Record selection before redirecting
      updateStepData('choose-subscription', {
        selectedTier: tier.id,
        selectedAt: new Date().toISOString(),
        checkoutSessionId: response.session_id,
      });

      // Redirect to Stripe (backend returns checkout_url)
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        // No checkout URL returned - reset processing state
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      setError('Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Choose Your Plan</h2>
        <p style={styles.subtitle}>
          Start free and upgrade anytime. Pro includes a 7-day free trial.
        </p>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      <div style={styles.tiers}>
        {SUBSCRIPTION_TIERS.map((tier) => (
          <div
            key={tier.id}
            style={{
              ...styles.tierCard,
              ...(tier.highlighted ? styles.tierCardHighlighted : {}),
              ...(selectedTier === tier.id ? styles.tierCardSelected : {}),
            }}
          >
            {tier.badge && (
              <div style={styles.badge}>{tier.badge}</div>
            )}

            <div style={styles.tierHeader}>
              <h3 style={styles.tierName}>{tier.name}</h3>
              <div style={styles.tierPrice}>
                <span style={styles.priceAmount}>{tier.price}</span>
                {tier.period && (
                  <span style={styles.pricePeriod}>{tier.period}</span>
                )}
              </div>
              <p style={styles.tierDescription}>{tier.description}</p>
            </div>

            <ul style={styles.featureList}>
              {tier.features.map((feature, i) => (
                <li key={i} style={styles.featureItem}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tokens.colors.success[500]} strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSelectTier(tier)}
              disabled={isProcessing}
              style={{
                ...styles.selectButton,
                ...(tier.highlighted ? styles.selectButtonHighlighted : {}),
                opacity: isProcessing && selectedTier === tier.id ? 0.7 : 1,
              }}
            >
              {isProcessing && selectedTier === tier.id
                ? 'Processing...'
                : tier.stripePriceId
                  ? 'Start Trial'
                  : 'Get Started Free'
              }
            </button>
          </div>
        ))}
      </div>

      {/* Enterprise note */}
      <p style={styles.enterpriseNote}>
        Need more?{' '}
        <a href="mailto:sales@cahoots.cc" style={styles.link}>
          Contact us
        </a>
        {' '}for Enterprise pricing.
      </p>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: tokens.colors.neutral[100],
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '15px',
    color: tokens.colors.neutral[400],
    lineHeight: '1.5',
  },
  error: {
    padding: '12px 16px',
    backgroundColor: `${tokens.colors.error[500]}15`,
    border: `1px solid ${tokens.colors.error[500]}30`,
    borderRadius: '8px',
    color: tokens.colors.error[400],
    fontSize: '14px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  tiers: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    overflowX: 'auto',
    paddingBottom: '8px',
  },
  tierCard: {
    flex: '1 1 0',
    minWidth: '160px',
    padding: '20px',
    backgroundColor: tokens.colors.neutral[850] || tokens.colors.neutral[800],
    borderRadius: '12px',
    border: `1px solid ${tokens.colors.neutral[700]}`,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    transition: 'border-color 0.15s ease, transform 0.15s ease',
  },
  tierCardHighlighted: {
    borderColor: tokens.colors.primary[500],
    backgroundColor: `${tokens.colors.primary[500]}08`,
  },
  tierCardSelected: {
    transform: 'scale(1.02)',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 10px',
    backgroundColor: tokens.colors.primary[500],
    color: 'white',
    fontSize: '11px',
    fontWeight: '700',
    borderRadius: '999px',
    whiteSpace: 'nowrap',
    marginBottom: '10px',
  },
  tierHeader: {
    marginBottom: '16px',
  },
  tierName: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colors.neutral[100],
    marginBottom: '8px',
  },
  tierPrice: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: '8px',
  },
  priceAmount: {
    fontSize: '28px',
    fontWeight: '700',
    color: tokens.colors.neutral[100],
  },
  pricePeriod: {
    fontSize: '14px',
    color: tokens.colors.neutral[500],
    marginLeft: '4px',
  },
  tierDescription: {
    fontSize: '13px',
    color: tokens.colors.neutral[500],
    lineHeight: '1.4',
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    marginBottom: '16px',
    flex: 1,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '13px',
    color: tokens.colors.neutral[300],
    marginBottom: '8px',
    lineHeight: '1.4',
  },
  selectButton: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: tokens.colors.neutral[700],
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colors.neutral[100],
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  selectButtonHighlighted: {
    backgroundColor: tokens.colors.primary[500],
    color: 'white',
  },
  enterpriseNote: {
    textAlign: 'center',
    fontSize: '13px',
    color: tokens.colors.neutral[500],
  },
  link: {
    color: tokens.colors.primary[400],
    textDecoration: 'underline',
  },
};

export default ChooseSubscriptionStep;
